import { zodResolver } from "@hookform/resolvers/zod";
import { EnvelopeSimpleIcon, PhoneCallIcon } from "@phosphor-icons/react";
import { useTranslation } from "next-i18next";
import * as Sentry from "@sentry/nextjs";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { AiOutlineLoading } from "react-icons/ai";
import { FaWhatsapp } from "react-icons/fa";
import { PhoneInput } from "react-international-phone";
import z from "zod";
import { size } from "../../constants";
import { useWindowSize } from "../../hooks/useWindowResize";
import { cn } from "../../lib/utils";
import { locations } from "../ContactSection";
import {
	BannerImg,
	BannerImgWrapper,
	ContactIcon,
	ContactIcons,
	Container,
	Flag,
	HighlightBar,
	Location,
	LocationPhone,
	LocationsGrid,
	LocationsText,
	LocationsWrapper,
	LocationTitle,
	Section,
	Text,
	Title,
} from "./styles";

// Lista de provedores de e-mail gratuitos
const FREE_EMAIL_PROVIDERS = [
	"gmail.com",
	"hotmail.com",
	"outlook.com",
	"yahoo.com",
	"yahoo.com.br",
	"live.com",
	"icloud.com",
	"me.com",
	"aol.com",
	"protonmail.com",
	"mail.com",
	"gmx.com",
	"yandex.com",
	"zoho.com",
	"inbox.com",
	"mail.ru",
	"terra.com.br",
	"bol.com.br",
	"uol.com.br",
	"ig.com.br",
	"r7.com",
	"oi.com.br",
];

const schema = z.object({
	name: z.string().min(1, "nameRequired"),
	email: z
		.string()
		.min(1, "emailRequired")
		.email("emailInvalid")
		.refine((email) => {
			const domain = email.split("@")[1]?.toLowerCase();
			return !FREE_EMAIL_PROVIDERS.includes(domain);
		}, "emailMustBeCorporate"),
	phone: z
		.string()
		.min(1, "phoneRequired")
		.refine((phone) => {
			// Remove espaços e caracteres especiais para validação
			const cleanPhone = phone.replace(/[\s\-()]/g, "");
			// Deve ter pelo menos +XX e mais 8 dígitos (formato mínimo internacional)
			return cleanPhone.length >= 11 && cleanPhone.startsWith("+");
		}, "phoneMinLength"),
	company: z.string().min(1, "companyRequired"),
	subject: z.string().min(1, "subjectRequired"),
});

const Input = React.forwardRef(({ className, type, ...props }, ref) => {
	return (
		<input
			type={type}
			className={cn(
				"border border-white rounded-lg px-4 bg-neutral-08 text-white placeholder:text-neutral-02 focus:outline-none",
				"text-base leading-5 h-[48px]",
				"laptop:text-lg laptop:leading-6 laptop:h-[56px]",
				className,
			)}
			ref={ref}
			{...props}
		/>
	);
});
Input.displayName = "Input";

export default function ContactSectionForm({ title, description, isMobile }) {
	const { t, i18n } = useTranslation();
	const [phone, setPhone] = useState(""); // Iniciar vazio para mostrar placeholder
	const [phoneTouched, setPhoneTouched] = useState(false); // Controlar se o campo foi tocado

	const { width } = useWindowSize();
	const isMobileSize = width < size.laptop;

	const form = useForm({
		defaultValues: {
			name: "",
			email: "",
			phone: "",
			company: "",
			subject: "",
		},
		mode: "onTouched", // Validar apenas após o usuário tocar no campo
		resolver: zodResolver(schema),
	});

	const {
		register,
		reset,
		handleSubmit: hookFormHandleSubmit,
		formState,
		setValue,
		trigger,
	} = form;

	// Atualizar o valor do telefone no form quando o PhoneInput mudar
	useEffect(() => {
		// Se o telefone estiver vazio ou for apenas espaços, garantir que tenha pelo menos o DDI do país selecionado
		const phoneValue = phone || "";
		setValue("phone", phoneValue);
		if (phoneTouched) {
			trigger("phone"); // Revalidar o campo apenas se foi tocado
		}
	}, [phone, setValue, trigger, phoneTouched]);

	const handleSubmit = async (data) => {
		try {
			//alter data
			// Check if ABM=true query parameter exists
			const urlParams = new URLSearchParams(window.location.search);
			const isABM = urlParams.get('ABM') === 'true';
			const origin = isABM ? 'SITE ABM' : 'SITE';

			const headers = new Headers();
			headers.append("Content-Type", "application/json");
			headers.append(
				"X-Api-Key",
				process.env.NEXT_PUBLIC_VERZEL_LAMBDA_FUNCTIONS_KEY,
			);

			const response = await fetch(
				`${process.env.NEXT_PUBLIC_VERZEL_LAMBDA_FUNCTIONS_HOST}/contact-us`,
				{
					method: "POST",
					headers,
					body: JSON.stringify({
						...data,
						phone: phone, // Enviar o telefone com DDI completo
						origin: origin,
					}),
				},
			);

			if (response.ok) {
				toast.success(
					t("contactUs.contactSuccess", {
						duration: 2000,
						position: "bottom-center",
					}),
				);
				form.reset();
				setPhone(""); // Resetar o telefone para vazio (mostrará placeholder)
				setPhoneTouched(false); // Resetar o estado de touched
			}
		} catch (error) {
			console.error(error);
			Sentry.captureException(error, {
				tags: { form: 'contact-section-form' },
				extra: { 
					apiUrl: `${process.env.NEXT_PUBLIC_VERZEL_LAMBDA_FUNCTIONS_HOST}/contact-us`,
					formData: { name: data.name, email: data.email, phone: phone }
				}
			});
			toast.error(t("contactUs.error.send"), {
				duration: 2000,
				position: "bottom-center",
			});
		}
	};

	return (
		<Section id="contact-us">
			<div className="container-blog">
				<Container id="contact-form" className="mx-auto relative">
					<HighlightBar />
					<Title dangerouslySetInnerHTML={{ __html: title }} />
					<p className="text-white text-xl leading-9 m-0 mt-4 mb-2 text-center md:text-left max-w-[635px] mx-auto md:!mx-0">
						{t("contactUs.whatsappResponseTime")
							.split("WhatsApp")
							.map((part, index, array) => (
								<React.Fragment key={index}>
									{part}
									{index < array.length - 1 && (
										<>
											WhatsApp{" "}
											<img
												src="/blog/assets/icons/whatsapp-white.svg"
												alt="WhatsApp"
												className="w-4 h-4 inline-block mx-1"
											/>
										</>
									)}
								</React.Fragment>
							))}
					</p>
					{description && (
						<Text
							className="sr-only"
							dangerouslySetInnerHTML={{ __html: description }}
						/>
					)}

					<form className="flex flex-col gap-6 max-w-[620px] mt-6 mx-auto md:!mx-0">
						<div
							className={cn("flex relative", {
								"mb-3": formState.errors.name,
							})}
						>
							<Input
								className={cn("w-full", {
									"!border-red-500": formState.errors.name,
								})}
								type="text"
								placeholder={t("contactUs.placeholders.name")}
								{...register("name")}
							/>
							{formState.errors.name && (
								<p className="absolute bottom-0 translate-y-full left-0 text-red-500 m-0 text-xs pt-1">
									{t(`contactUs.validation.${formState.errors.name.message}`)}
								</p>
							)}
						</div>
						<div
							className={cn("flex relative", {
								"mb-6": formState.errors.email,
							})}
						>
							<Input
								className={cn("w-full", {
									"!border-red-500": formState.errors.email,
								})}
								type="email"
								placeholder={t("contactUs.placeholders.email")}
								{...register("email")}
							/>
							{formState.errors.email && (
								<p className="absolute bottom-0 translate-y-full left-0 text-red-500 m-0 text-xs pt-1">
									{formState.errors.email.message === "emailMustBeCorporate"
										? t("contactUs.validation.emailMustBeCorporate")
										: t(
												`contactUs.validation.${formState.errors.email.message}`,
											)}
								</p>
							)}
						</div>
						<div
							className={cn("flex relative", {
								"mb-3": phoneTouched && formState.errors.phone,
							})}
						>
							<PhoneInput
								defaultCountry="br"
								value={phone}
								onChange={(phone) => {
									setPhone(phone);
								}}
								onBlur={() => {
									setPhoneTouched(true);
									trigger("phone"); // Forçar validação ao sair do campo
								}}
								className={cn("w-full", {
									"phone-input-error": phoneTouched && formState.errors.phone,
								})}
							/>
							{phoneTouched && formState.errors.phone && (
								<p className="absolute bottom-0 translate-y-full left-0 text-red-500 m-0 text-xs pt-1">
									{t(`contactUs.validation.${formState.errors.phone.message}`)}
								</p>
							)}
						</div>
						<div
							className={cn("flex relative", {
								"mb-3": formState.errors.company,
							})}
						>
							<Input
								className={cn("w-full", {
									"!border-red-500": formState.errors.company,
								})}
								type="text"
								placeholder={t("contactUs.placeholders.company")}
								{...register("company")}
							/>
							{formState.errors.company && (
								<p className="absolute bottom-0 translate-y-full left-0 text-red-500 m-0 text-xs pt-1">
									{t(
										`contactUs.validation.${formState.errors.company.message}`,
									)}
								</p>
							)}
						</div>

						<div
							className={cn("flex relative", {
								"mb-3": formState.errors.subject,
							})}
						>
							<textarea
								placeholder={t("contactUs.placeholders.message")}
								className={cn(
									"h-[96px] text-base leading-5 w-full border border-white rounded-lg p-4 bg-neutral-08 text-white placeholder:text-neutral-02 focus:outline-none resize-none",
									"laptop:text-lg laptop:leading-6",
									{
										"!border-red-500": formState.errors.subject,
									},
								)}
								{...register("subject")}
							/>
							{formState.errors.subject && (
								<p className="absolute bottom-0 translate-y-full left-0 text-red-500 m-0 text-xs pt-1">
									{t(
										`contactUs.validation.${formState.errors.subject.message}`,
									)}
								</p>
							)}
						</div>
					</form>

					<div className="start-chat-wrapper flex gap-4 max-w-[560px] mx-auto md:!mx-0">
						<button
							onClick={hookFormHandleSubmit(handleSubmit)}
							type="button"
							disabled={!formState.isValid || formState.isSubmitting}
							className={cn(
								"h-[48px] w-full border border-white rounded-full px-4 flex items-center justify-center leading-[40px] transition-opacity",
								"laptop:w-[183px] laptop:h-[56px]",
								{
									"opacity-50 cursor-not-allowed":
										!formState.isValid || formState.isSubmitting,
								},
							)}
						>
							{formState.isSubmitting && (
								<AiOutlineLoading className="w-6 h-6 animate-spin" />
							)}

							{!formState.isSubmitting && (
								<span className="text-sm laptop:text-lg leading-[40px] font-semibold uppercase text-white tracking-[1.4px]">
									{t("contactUs.inputField.send")}
								</span>
							)}
						</button>
						{/* <WhatsappStartChatButton ghost /> */}
					</div>

					<div
						className={cn(
							"laptop:absolute laptop:top-0 laptop:right-0 laptop:h-full laptop:w-full -z-[1]",
						)}
					>
						<img
							className={cn(
								"mt-[22px] mx-auto",
								"laptop:h-full laptop:w-full object-cover laptop:mt-[78px]",
							)}
							src={
								!isMobileSize
									? "/landings/assets/images/products/contact-laptop.png"
									: "/landings/assets/images/products/contact-laptop-mobile.png"
							}
							alt="Notebook"
						/>
					</div>
				</Container>

				<LocationsWrapper>
					<LocationsText
						dangerouslySetInnerHTML={{
							__html: !isMobile
								? t("contactUs.locations.description.desktop")
								: t("contactUs.locations.description.mobile"),
						}}
					/>
					<LocationsGrid>
						{locations.map((location) => (
							<Location
								key={location.location}
								itemScope
								itemType="https://schema.org/Organization"
							>
								<Flag src={location.flag} alt={location.location} />
								<LocationTitle itemProp="address">
									{location.location}
								</LocationTitle>
								<LocationPhone
									itemProp="telephone"
									content={location.contacts.phoneNumber}
								>
									{location.phone}
								</LocationPhone>
								<ContactIcons>
									<ContactIcon
										href={`tel:${location.contacts.phoneNumber}`}
										aria-label={`Ligar para ${location.location}`}
										title={`Ligar para ${location.location}`}
										itemProp="telephone"
									>
										<PhoneCallIcon size={24} />
									</ContactIcon>
									<ContactIcon
										href={`https://wa.me/${location.contacts.whatsappNumber}`}
										target="_blank"
										rel="noopener noreferrer"
										aria-label={`WhatsApp ${location.location}`}
										title={`WhatsApp ${location.location}`}
									>
										<FaWhatsapp />
									</ContactIcon>
									<ContactIcon
										href={`mailto:${location.contacts.email}`}
										aria-label={`Enviar e-mail para ${location.location}`}
										title={`Enviar e-mail para ${location.location}`}
										itemProp="email"
									>
										<EnvelopeSimpleIcon size={24} />
									</ContactIcon>
								</ContactIcons>
								<meta itemProp="name" content="Verzel" />
								<meta itemProp="url" content="https://verzel.com.br" />
							</Location>
						))}
					</LocationsGrid>
				</LocationsWrapper>
			</div>
		</Section>
	);
}
